import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { putClientPolicyApplicationStepRequest } from '@client/model';
import type { IllustrationMetadata } from './useIllustrationMetadata';

interface UsePolicyApplicationStepActionsProps {
  clientPolicyApplicationId: string;
  stepType: PolicyApplicationStepType;
  hasIllustrationError: boolean;
  buildIllustrationMetadata: () => IllustrationMetadata | null;
  onContinue: () => void;
}

export function usePolicyApplicationStepActions({
  clientPolicyApplicationId,
  stepType,
  hasIllustrationError,
  buildIllustrationMetadata,
  onContinue,
}: UsePolicyApplicationStepActionsProps) {
  const dispatch = useDispatch();

  const handleSaveIllustrationError = useCallback(() => {
    const metadata = buildIllustrationMetadata();
    if (!metadata) return;

    dispatch(
      putClientPolicyApplicationStepRequest({
        clientPolicyApplicationId,
        stepType,
        status: 'error',
        metadata,
      }),
    );
  }, [buildIllustrationMetadata, dispatch, clientPolicyApplicationId, stepType]);

  const handleSkipStep = useCallback(() => {
    // If skipping due to error, include error metadata
    const metadata = hasIllustrationError ? buildIllustrationMetadata() : undefined;

    dispatch(
      putClientPolicyApplicationStepRequest({
        clientPolicyApplicationId,
        stepType,
        status: 'skipped',
        metadata,
      }),
    );

    if (stepType === 'download-policy-illustration') {
      dispatch(
        putClientPolicyApplicationStepRequest({
          clientPolicyApplicationId,
          stepType: 'preapplication-consent-form',
          status: 'skipped',
          metadata: undefined,
        }),
      );
    }

    onContinue();
  }, [
    dispatch,
    clientPolicyApplicationId,
    stepType,
    onContinue,
    hasIllustrationError,
    buildIllustrationMetadata,
  ]);

  return {
    handleSaveIllustrationError,
    handleSkipStep,
  };
}
